import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { normalizeMarkdown } from "@/lib/markdown";

/**
 * MarkdownView - the ONE canonical markdown renderer for NovaSynth.
 *
 * Wraps ReactMarkdown + remark-gfm with a single styled `components` map and
 * always runs the text through `normalizeMarkdown()` first (repairs streamed
 * run-on headers/bullets, strips forbidden em/en dashes, etc). Every surface
 * that shows model output - the synthesis brief, the per-agent cards, and the
 * battlecard - renders through this so behaviour stays consistent:
 *
 *   - GFM tables render as a real bordered, horizontally-scrollable table that
 *     fits the dark theme (not raw "| col | col |" text).
 *   - `[text](url)` renders as a cyan, underlined link that opens in a new tab
 *     with rel="noopener noreferrer" (not literal markdown).
 *   - Headings, paragraphs, lists, strong/em, inline code, blockquote and rules
 *     are all themed for the OLED surface and AA-readable.
 *
 * The wrapping `.prose prose-invert` gives sane vertical rhythm; the component
 * overrides keep the colours on-brand.
 */

interface MarkdownViewProps {
  children: string;
  /**
   * Slightly tunes spacing/emphasis per surface. "brief" is the roomy default
   * used by the synthesis output; "compact" matches the tighter agent/battlecard
   * cards. Purely cosmetic - the element set + link/table behaviour is identical.
   */
  variant?: "brief" | "compact";
  className?: string;
}

export function MarkdownView({ children, variant = "brief", className = "" }: MarkdownViewProps) {
  const compact = variant === "compact";

  return (
    <div className={`prose prose-invert prose-sm max-w-none leading-relaxed select-text ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 underline hover:text-cyan-300 break-all transition-colors"
            >
              {children}
            </a>
          ),
          p: ({ children }) => (
            <p className="mb-2.5 last:mb-0 text-slate-300 text-sm leading-relaxed">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-outside pl-5 space-y-1.5 mb-2.5 marker:text-cyan-500/60">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-outside pl-5 space-y-1.5 mb-2.5 marker:text-cyan-500/60">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-slate-300 text-sm leading-relaxed pl-1">{children}</li>
          ),
          h1: ({ children }) => (
            <h1 className={`font-bold text-white ${compact ? "text-base mt-3 mb-1.5" : "text-lg mt-4 mb-2"}`}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              className={`font-semibold text-white border-b border-[#1a2640] pb-1.5 ${
                compact ? "text-sm mt-4 mb-1.5" : "text-base mt-6 mb-2"
              }`}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-100 mt-5 mb-1.5">
              <span className="w-1 h-4 bg-brand-500 rounded-full shrink-0" aria-hidden="true" />
              {children}
            </h3>
          ),
          strong: ({ children }) => <strong className="font-semibold text-slate-100">{children}</strong>,
          em: ({ children }) => <em className="italic text-slate-200">{children}</em>,
          code: ({ children }) => (
            <code className="font-mono text-xs bg-[#1a2640] text-[#22d3ee] px-1.5 py-0.5 rounded">
              {children}
            </code>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-cyan-500/40 pl-3 my-2 text-slate-400 italic">
              {children}
            </blockquote>
          ),
          // GFM table -> real bordered table, scrolls horizontally on mobile.
          table: ({ children }) => (
            <div className="overflow-x-auto my-3 rounded-lg border border-white/10">
              <table className="w-full text-xs border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-white/5">{children}</thead>,
          th: ({ children }) => (
            <th className="border border-white/10 px-2.5 py-1.5 text-left font-semibold text-slate-200 whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-white/10 px-2.5 py-1.5 text-slate-300 align-top">{children}</td>
          ),
          hr: () => <hr className="border-white/10 my-3" />,
        }}
      >
        {normalizeMarkdown(children)}
      </ReactMarkdown>
    </div>
  );
}
